import React from 'react';

/**
 * Instagram-style verification seal shown next to IG handles across the app.
 *
 *  - `claimed` (green)  — a real user has claimed & verified ownership of this
 *    handle in-app (creator_claims / advertiser_claims verified). Outranks the
 *    scraped blue check: if a handle is both claimed and IG-verified we show green.
 *  - `verified` (blue)  — the scraped Instagram profile carries the blue check
 *    (is_verified / verified / um_is_verified from the Apify profile scrape).
 *
 * Renders nothing when neither is true, so it's safe to drop in unconditionally:
 *   <VerifiedBadge verified={r.um_is_verified} claimed={r.claimed} />
 */
export function VerifiedBadge({
  verified,
  claimed,
  size = 15,
  style,
}: {
  verified?: boolean | null;
  claimed?: boolean | null;
  size?: number;
  style?: React.CSSProperties;
}) {
  const kind: 'claimed' | 'verified' | null = claimed ? 'claimed' : verified ? 'verified' : null;
  if (!kind) return null;

  const fill = kind === 'claimed' ? '#2ecc71' : '#3897f0';
  const title = kind === 'claimed'
    ? 'Claimed — a verified account owner controls this handle in AdaMar'
    : 'Verified on Instagram (scraped blue check)';

  return (
    <span
      title={title}
      aria-label={title}
      style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0, verticalAlign: 'middle', ...style }}
    >
      {/* 12-point starburst seal + white check, matching Instagram's verified glyph. */}
      <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-hidden="true">
        <path
          fill={fill}
          d="M12 1.5l2.31 2.06 3.06-.53.99 2.95 2.95.99-.53 3.06L23.5 12l-2.06 2.31.53 3.06-2.95.99-.99 2.95-3.06-.53L12 22.5l-2.31-2.06-3.06.53-.99-2.95-2.95-.99.53-3.06L1.5 12l2.06-2.31-.53-3.06 2.95-.99.99-2.95 3.06.53L12 1.5z"
        />
        <path
          fill="#fff"
          d="M10.6 15.2l-2.9-2.9 1.27-1.27 1.63 1.63 3.96-3.96 1.27 1.27-5.23 5.23z"
        />
      </svg>
    </span>
  );
}

export default VerifiedBadge;
